import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Phone,
  Mail,
  MessageSquare,
  Video,
  MoreVertical,
  AlertTriangle,
  UserX,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { TeacherInfoTab } from './TeacherInfoTab';
import { TeacherCoursesTab } from './TeacherCoursesTab';
import { TeacherHoursTab } from './TeacherHoursTab';
import { TeacherLibraryTab } from './TeacherLibraryTab';
import { TeacherFinanceTab } from './TeacherFinanceTab';

type TabType = 'info' | 'courses' | 'hours' | 'library' | 'finance';

interface TeacherData {
  id: string;
  name: string;
  email: string;
  phone: string;
  avatar: string;
  role: string;
  status: 'active' | 'inactive';
  joinDate: string;
  address: string;
  dateOfBirth: string;
  gender: string;
  idNumber: string;
  subjects: string[];
  isSchoolAdmin: boolean;
}

const mockTeacher: TeacherData = {
  id: '1',
  name: 'Teacher Name',
  email: 'example@email.com',
  phone: '+972 5244444',
  avatar: '',
  role: 'Science Teacher',
  status: 'active',
  joinDate: '2024-01-15',
  address: 'Address of the Current body',
  dateOfBirth: '1985-03-15',
  gender: 'Male',
  idNumber: '30-0000',
  subjects: ['Physics', 'Chemistry', 'Biology'],
  isSchoolAdmin: false,
};

export function TeacherProfilePage() {
  const { teacherId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('info');
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [showDismissModal, setShowDismissModal] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [teacher] = useState<TeacherData>(mockTeacher);

  const tabs: { id: TabType; label: string }[] = [
    { id: 'info', label: 'Teacher Info' },
    { id: 'courses', label: 'Courses' },
    { id: 'hours', label: 'Hours' },
    { id: 'library', label: 'Library' },
    { id: 'finance', label: 'Finance' },
  ];

  const handleDismissTeacher = () => {
    setShowDismissModal(true);
    setShowContextMenu(false);
  };

  const handleConfirmDismiss = () => {
    console.log('Teacher dismissed');
    setShowDismissModal(false);
    navigate('/admin/teachers');
  };

  return (
    <div className="p-6 space-y-6">
      {/* Back Button */}
      <button
        onClick={() => navigate('/admin/teachers')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="w-4 h-4" />
        <span>Back to Teachers</span>
      </button>

      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-6">
            {/* Avatar */}
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white text-3xl font-bold">
              {teacher.avatar ? (
                <img src={teacher.avatar} alt={teacher.name} className="w-full h-full rounded-full object-cover" />
              ) : (
                teacher.name.charAt(0)
              )}
            </div>

            {/* Info */}
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{teacher.name}</h1>
              <p className="text-gray-500">{teacher.address}</p>
              <div className="flex items-center gap-4 mt-2">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  teacher.status === 'active'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {teacher.status === 'active' ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          {/* Contact Info & Actions */}
          <div className="flex items-start gap-8">
            {/* Contact Details */}
            <div className="text-right">
              <p className="text-sm text-gray-500">Phone</p>
              <p className="font-medium text-gray-900">{teacher.phone}</p>
              <p className="text-sm text-gray-500 mt-2">Email</p>
              <p className="font-medium text-gray-900">{teacher.email}</p>
              <p className="text-sm text-gray-500 mt-2">ID</p>
              <p className="font-medium text-gray-900">{teacher.idNumber}</p>
            </div>

            {/* Quick Actions */}
            <div className="flex items-center gap-2">
              <button className="p-2 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors">
                <Phone className="w-5 h-5" />
              </button>
              <button className="p-2 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors">
                <Mail className="w-5 h-5" />
              </button>
              <button className="p-2 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors">
                <MessageSquare className="w-5 h-5" />
              </button>
              <button className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-2">
                <Video className="w-4 h-4" />
                Join Session
              </button>
              <div className="relative">
                <button
                  onClick={() => setShowContextMenu(!showContextMenu)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  <MoreVertical className="w-5 h-5" />
                </button>

                {showContextMenu && (
                  <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                    <button
                      onClick={() => {
                        setIsMuted(!isMuted);
                        setShowContextMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    >
                      {isMuted ? (
                        <>
                          <Volume2 className="w-4 h-4" />
                          Unmute Teacher
                        </>
                      ) : (
                        <>
                          <VolumeX className="w-4 h-4" />
                          Mute Teacher
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleDismissTeacher}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <UserX className="w-4 h-4" />
                      Dismiss Teacher
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Primary Actions */}
        <div className="flex items-center gap-3 mt-6">
          <button
            onClick={handleDismissTeacher}
            className="px-4 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 flex items-center gap-2"
          >
            <UserX className="w-4 h-4" />
            Dismiss Teacher
          </button>
          <button className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600">
            VIS Input
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-6 mt-6 border-t border-gray-200 pt-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-2 text-sm font-medium border-b-2 -mb-[17px] transition-colors ${
                activeTab === tab.id
                  ? 'border-teal-600 text-teal-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'info' && <TeacherInfoTab teacher={teacher} />}
      {activeTab === 'courses' && <TeacherCoursesTab teacherId={teacher.id} />}
      {activeTab === 'hours' && <TeacherHoursTab teacherId={teacher.id} />}
      {activeTab === 'library' && <TeacherLibraryTab teacherId={teacher.id} />}
      {activeTab === 'finance' && <TeacherFinanceTab teacherId={teacher.id} teacherName={teacher.name} />}

      {/* Click outside to close context menu */}
      {showContextMenu && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowContextMenu(false)}
        />
      )}

      {/* Dismiss Modal */}
      {showDismissModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Dismiss Teacher</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>

            <p className="text-gray-600 mb-4">
              Are you sure you want to dismiss <strong>{teacher.name}</strong> from the school?
              This will remove their access to all courses and school resources.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for dismissal
              </label>
              <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500">
                <option value="">Select a reason</option>
                <option value="resignation">Resignation</option>
                <option value="contract_end">Contract End</option>
                <option value="performance">Performance Issues</option>
                <option value="misconduct">Misconduct</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional notes (optional)
              </label>
              <textarea
                rows={3}
                placeholder="Add any additional notes..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowDismissModal(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDismiss}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Dismiss Teacher
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
